import { FormEvent, useEffect, useReducer, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import {
  INITIAL_STATE,
  SESSION_WAIT_MS,
  SIGN_IN_PATH,
  beginAccept,
  choosePasswordLead,
  messageForFailure,
  nextStep,
  scrubTarget,
} from "../lib/acceptInvite";

// The invite callback (F7 §4.4, ADR-36; code exchange per architect ruling
// Q3, 250926). An invited consultant's email links here directly with a
// single-use token hash in the QUERY (supabase/templates/invite.html). This
// route clears the URL, exchanges the hash through verifyOtp, has the user
// choose a password, signs out, and lands on sign-in — so the first real
// session is an ordinary password sign-in (G20).
//
// It renders outside the session gate and outside BrowserRouter (see
// App.tsx), because it is reached signed OUT.
//
// ORDERING — the URL is cleared before anything else happens.
//
// `start()` below runs from the reducer's initializer, i.e. during the
// first render and before it is committed. beginAccept reads the landing,
// replaceStates the address bar to the bare route, and only then calls
// verifyOtp. The landing history entry is rewritten in place, so Back and
// Forward can only ever reach `/auth/accept` with nothing on it — which is
// token_missing, never a second exchange. The auth client does not read the
// URL at all (`detectSessionInUrl: false`, src/lib/supabase.ts): the
// implicit flow, whose session sat in the fragment and whose own hash
// assignment left a tokened history entry behind, is removed rather than
// defended, and a fragment that still carries a token is refused.
//
// `start()` is memoized per document, not per mount: StrictMode renders
// and mounts twice in development, and the single-use token must be
// exchanged exactly once. A new document (reload, Forward to the bare
// route) starts afresh from a URL that no longer carries a token.
//
// HYGIENE (G18a "nothing logged"): no console call anywhere in this file or
// in lib/acceptInvite.ts (test/no-console-output.test.ts pins it), no token
// value is ever held in React state (beginAccept returns none), and every
// message on screen is a fixed string chosen through an allowlist.

const PASSWORD_FIELD_ID = "new-password";
const CONFIRM_FIELD_ID = "confirm-password";

let begun: ReturnType<typeof beginAccept> | null = null;

function start(): ReturnType<typeof beginAccept> {
  if (begun === null) {
    begun = beginAccept({
      search: window.location.search,
      hash: window.location.hash,
      strip: () => window.history.replaceState(null, "", scrubTarget(window.location.href)),
      verifyOtp: (params) => supabase.auth.verifyOtp(params),
    });
  }
  return begun;
}

export function AcceptInvite() {
  const [state, dispatch] = useReducer(nextStep, undefined, () =>
    nextStep(INITIAL_STATE, { type: "landed", landing: start().landing }),
  );
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  // The password the reducer accepted, read by the updating effect. A ref,
  // not a dependency: StrictMode re-runs effects in development and the
  // request must go out exactly once per entry into "updating".
  const accepted = useRef("");
  const started = useRef<string | null>(null);

  // awaiting_session: wait for the exchange's outcome, or give up after
  // SESSION_WAIT_MS. The URL was already cleared by start(). The reducer
  // ignores a duplicate or late event, so StrictMode's second mount and a
  // timer racing the outcome are both harmless.
  useEffect(() => {
    if (state.step !== "awaiting_session") return;
    let live = true;
    start().outcome?.then((event) => {
      if (live) dispatch(event);
    });
    const timer = window.setTimeout(() => {
      if (live) dispatch({ type: "timeout" });
    }, SESSION_WAIT_MS);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [state.step]);

  // failed: make sure the address bar is the bare route (start() cleared it
  // already; this is belt and braces). The ONLY session this page may end is
  // one its own exchange produced after the timeout. It never listens to the
  // auth client's state and never signs out on entering this state: the
  // storage is shared across the tabs of one window, so a listener here saw
  // a session that ANOTHER tab had just signed in with and revoked it on the
  // server (supabase-js's local sign-out still calls /logout) — the 2026-10-28
  // loop where every sign-in "asked again" while an expired-link tab stayed
  // open (RELEASE_RECORD_F7_FIRST_CLIENT 271026 V1.0.md, step 7).
  useEffect(() => {
    if (state.step !== "failed") return;
    window.history.replaceState(null, "", scrubTarget(window.location.href));
    let live = true;
    start().outcome?.then((event) => {
      if (live && event.type === "session") void supabase.auth.signOut({ scope: "local" });
    });
    return () => {
      live = false;
    };
  }, [state.step]);

  // updating: one updateUser per entry into the state.
  useEffect(() => {
    if (state.step !== "updating" || started.current === "updating") return;
    started.current = "updating";
    supabase.auth.updateUser({ password: accepted.current }).then(({ error }) => {
      started.current = null;
      dispatch(error ? { type: "update_failed", code: error.code ?? null } : { type: "update_ok" });
    });
  }, [state.step]);

  // continue: the respondent is signed in — by the magic link's exchange, or
  // by the invite/recovery exchange with the password now set — and "/" loads
  // as a NEW document with that session kept (DESIGN_SLICE_INVITE_SCREEN
  // 281026 V1.0, drawing 3: "the click signed you in"). location.assign
  // rather than a router navigation: this component renders outside
  // BrowserRouter, and a fresh document leaves nothing of this page behind.
  // The session gate then renders the respondent's assessment.
  useEffect(() => {
    if (state.step !== "continue" || started.current === "continue") return;
    started.current = "continue";
    window.location.assign("/");
  }, [state.step]);

  function submit(e: FormEvent) {
    e.preventDefault();
    accepted.current = password;
    dispatch({ type: "submit", password, confirm });
  }

  return (
    <div className="auth-page">
      <div className="card auth-card">
        <img className="logo" src="/brand/linnfoss-logo.svg" alt="LINNFOSS" />
        <h1 style={{ marginBottom: "0.25rem" }}>Assessments</h1>
        {state.step === "awaiting_session" && (
          <p style={{ color: "var(--text-muted)", marginTop: 0 }}>Checking your invitation…</p>
        )}
        {state.step === "failed" && (
          <>
            <p style={{ color: "var(--text-muted)", marginTop: 0 }}>Your invitation</p>
            <div className="error-banner">{messageForFailure(state.reason)}</div>
            <p style={{ marginBottom: 0 }}>
              <a href={SIGN_IN_PATH}>Go to sign-in</a>
            </p>
          </>
        )}
        {/* continue renders nothing: the magic link's exchange lands here for
            one frame before "/" loads, and the password form flashed in it
            (Kenneth, 2026-10-28: "password screen flashed 10 miliseconds and i
            was in"). The invite and recovery forms keep their own steps. */}
        {(state.step === "choose_password" ||
          state.step === "updating") && (
          <form onSubmit={submit}>
            <p style={{ color: "var(--text-muted)", marginTop: 0 }}>
              {choosePasswordLead(start().landing)}
            </p>
            {state.step === "choose_password" && state.problem && (
              <div className="error-banner">{state.problem}</div>
            )}
            <div className="field">
              <label htmlFor={PASSWORD_FIELD_ID}>New password</label>
              <input
                id={PASSWORD_FIELD_ID}
                type="password"
                autoComplete="new-password"
                required
                disabled={state.step !== "choose_password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor={CONFIRM_FIELD_ID}>Confirm password</label>
              <input
                id={CONFIRM_FIELD_ID}
                type="password"
                autoComplete="new-password"
                required
                disabled={state.step !== "choose_password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            <button
              className="btn"
              type="submit"
              disabled={state.step !== "choose_password"}
              style={{ width: "100%" }}
            >
              {state.step === "choose_password" ? "Set password" : "Setting password…"}
            </button>
            <p style={{ color: "var(--text-muted)", fontSize: "var(--lf-meta-size)", marginBottom: 0 }}>
              At least 12 characters. You continue to your assessment, signed in.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
